import { Component } from '@theme/component';
import { calculateHeaderGroupHeight } from '@theme/critical';
import { changeMetaThemeColor, onDocumentLoaded } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Scroll offset (px) before menu icons collapse on home/cohort pages.
   * Icons stay visible until user scrolls past this offset.
   * @constant {number}
   */
  static #menuIconsCollapseOffset = 250;

  /** @type {number | null} RAF id for throttling menu icons collapse update */
  #menuIconsRaf = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Whether a direction-change transition is currently animating
   * @type {boolean}
   */
  #isTransitioning = false;

  /**
   * Duration to lock direction changes after a transition starts (ms).
   * Should match or slightly exceed the CSS transition duration on the nav.
   * @constant {number}
   */
  #transitionLockDuration = 300;

  #menuCollapseLock = false;

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
        this.#updateMenuIconsCollapsed(true, '#observeStickyPosition');
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    // Check if we're on desktop (typically 750px+ breakpoint)
    const isDesktop = window.innerWidth >= 750;

    if (hideMenu && !isDesktop) {
      // Only hide menu on mobile/tablet, not on desktop
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  /**
   * Updates data-menu-icons-collapsed based on scroll offset (home/cohort only).
   * Icons hide only after scrolling past the offset. Throttled with RAF when called from scroll.
   * @param {boolean} [throttle] - If true, schedule update in next frame to avoid flicker
   */
  #updateMenuIconsCollapsed = (throttle = false, source = 'unknown') => {
    const run = () => {
      this.#menuIconsRaf = null;
      const isHomeOrCohort = !this.classList.contains('header--non-home-page');
      if (!isHomeOrCohort) return;

      const offset = parseInt(this.dataset.menuIconsOffset ?? '', 10) || HeaderComponent.#menuIconsCollapseOffset;
      const scrollTop = document.scrollingElement?.scrollTop ?? 0;
      const stickyActive = this.dataset.stickyState === 'active';

      const collapseOffset = offset + 10;
      const expandOffset = offset - 10;

      const isCollapsed = this.dataset.menuIconsCollapsed === 'true';

      const shouldCollapse = stickyActive && (isCollapsed ? scrollTop > expandOffset : scrollTop > collapseOffset);

      if (shouldCollapse && !isCollapsed) {
        this.#menuCollapseLock = true;

        const beforeHeight = this.getBoundingClientRect().height;
        this.dataset.menuIconsCollapsed = 'true';

        requestAnimationFrame(() => {
          const afterHeight = this.getBoundingClientRect().height;
          const diff = beforeHeight - afterHeight;

          if (diff !== 0) {
            document.scrollingElement.scrollTop += diff;
          }

          requestAnimationFrame(() => {
            this.#menuCollapseLock = false;
          });
        });

      } else if (!shouldCollapse && isCollapsed) {
        this.#menuCollapseLock = true;

        const beforeHeight = this.getBoundingClientRect().height;
        delete this.dataset.menuIconsCollapsed;

        requestAnimationFrame(() => {
          const afterHeight = this.getBoundingClientRect().height;
          const diff = beforeHeight - afterHeight;

          if (diff !== 0) {
            document.scrollingElement.scrollTop += diff;
          }

          requestAnimationFrame(() => {
            this.#menuCollapseLock = false;
          });
        });
      }
    };

    if (throttle) {
      if (this.#menuIconsRaf != null) return;
      this.#menuIconsRaf = requestAnimationFrame(run);
    } else {
      run();
    }
  };

  #handleWindowScroll = () => {
    if (this.#menuCollapseLock) return;

    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;

    if (scrollTop === this.#lastScrollTop) return;

    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const delta = Math.abs(scrollTop - this.#lastScrollTop);


    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      this.#updateMenuIconsCollapsed(true, '#handleWindowScroll');

      const isAtTop = scrollTop < 2;

      const currentDirection = this.dataset.scrollDirection;

      const threshold = currentDirection === 'up' ? 50 : 40;

      const nextDirection = isAtTop ? 'none' : isScrollingUp ? 'up' : 'down';
      const directionChanged = currentDirection !== nextDirection;

      if (nextDirection === 'none' || (delta >= threshold && !this.#isTransitioning)) {
        if (directionChanged) {
          this.dataset.scrollDirection = nextDirection;

          this.#isTransitioning = true;
          this.#timeout = setTimeout(() => {
            this.#isTransitioning = false;
            this.#timeout = null;
          }, this.#transitionLockDuration);
        }
      }
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        this.#offscreen = false;
        this.#setDataIfChanged('stickyState', 'inactive');
        this.#setDataIfChanged('scrollDirection', 'none');
      } else {
        this.#setDataIfChanged('stickyState', 'active');
        this.#setDataIfChanged('scrollDirection', 'up');
      }
    } else if (this.dataset.stickyState === 'active') {
      this.#setDataIfChanged('scrollDirection', 'none');
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.#setDataIfChanged('scrollDirection', 'none');
      this.#setDataIfChanged('stickyState', 'idle');
    }
    this.#lastScrollTop = scrollTop;
  };

  /**
   * Sets a dataset property only when the value is changing, avoiding unnecessary DOM mutations.
   * @param {string} key - The dataset key
   * @param {string} value - The new value
   */
  #setDataIfChanged(key, value) {
    if (this.dataset[key] !== value) {
      this.dataset[key] = value;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }

      if (stickyMode === 'always') {
        onDocumentLoaded(() => {
          this.#updateMenuIconsCollapsed(true, 'onDocumentLoaded');
        });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
